import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

export type Client2026MaturityProfileRow = {
  axe: number;
  title: string;
  asWas: number | null;
  asIs: number | null;
  toBe: number | null;
};

interface Client2026MaturitySpidersProps {
  profile: unknown;
}

const SERIES = [
  { key: 'asWas', label: 'Passé (As-Was)', color: '#64748b' },
  { key: 'asIs', label: 'Présent (As-Is)', color: '#0f766e' },
  { key: 'toBe', label: 'Futur (To-Be)', color: '#2563eb' },
] as const;

const isScore = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 5;

const normalizeProfile = (profile: unknown): Client2026MaturityProfileRow[] => {
  if (!Array.isArray(profile) || profile.length !== 6) return [];
  const rows = profile.flatMap((candidate): Client2026MaturityProfileRow[] => {
    if (!candidate || typeof candidate !== 'object') return [];
    const row = candidate as Record<string, unknown>;
    const axe = Number(row.axe);
    if (!Number.isInteger(axe) || axe < 1 || axe > 6 || typeof row.title !== 'string') return [];
    const values = [row.asWas, row.asIs, row.toBe];
    if (values.some((value) => value !== null && !isScore(value))) return [];
    return [{
      axe,
      title: row.title,
      asWas: row.asWas as number | null,
      asIs: row.asIs as number | null,
      toBe: row.toBe as number | null,
    }];
  });
  if (rows.length !== 6 || new Set(rows.map((row) => row.axe)).size !== 6) return [];
  return rows.sort((a, b) => a.axe - b.axe);
};

export default function Client2026MaturitySpiders({ profile }: Client2026MaturitySpidersProps) {
  const rows = normalizeProfile(profile);
  if (rows.length !== 6) return null;

  return (
    <section className="mb-8 rounded-xl border border-slate-200 bg-white p-5 shadow-sm print:break-inside-avoid">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-slate-900">Portrait de maturité TI</h2>
        <p className="mt-1 text-sm text-slate-600">
          Moyenne par axe sur 5. Au premier portrait, le Passé est figé à l’identique du Présent, puis conservé comme référence historique.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {SERIES.map((series) => {
          const chartData = rows.map((row) => ({
            axis: `Axe ${row.axe}`,
            fullTitle: row.title,
            score: row[series.key],
          }));
          const answered = chartData.filter((row) => row.score !== null).length;
          return (
            <div key={series.key} className="rounded-lg border border-slate-200 p-3">
              <h3 className="text-center text-sm font-semibold text-slate-800">{series.label}</h3>
              <p className="mt-1 text-center text-xs text-slate-500">{answered}/6 axes renseignés</p>
              {answered > 0 ? (
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={chartData} outerRadius="68%">
                      <PolarGrid stroke="#cbd5e1" />
                      <PolarAngleAxis dataKey="axis" tick={{ fill: '#475569', fontSize: 11 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 5]} tickCount={6} tick={{ fontSize: 9 }} />
                      <Radar
                        name={series.label}
                        dataKey="score"
                        stroke={series.color}
                        fill={series.color}
                        fillOpacity={0.24}
                        strokeWidth={2}
                        connectNulls={false}
                      />
                      <Tooltip
                        formatter={(value: number) => [`${Number(value).toFixed(2)} / 5`, series.label]}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.fullTitle || ''}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex h-64 items-center justify-center text-sm text-slate-500">
                  Données non précisées
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 grid gap-1 text-xs text-slate-600 md:grid-cols-2">
        {rows.map((row) => <div key={row.axe}><strong>Axe {row.axe} :</strong> {row.title}</div>)}
      </div>
    </section>
  );
}
